'use client'

import { ArrowLeft, ArrowRight } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import React, { useCallback, useEffect, useState } from 'react'

import { indiceCircular } from '@/components/agencia/gradeHelpers'
import { cn } from '@/utilities/ui'

export type Etapa = { rotulo: string; titulo?: string; texto: string }

type Props = { etapas: Etapa[]; autoplayMs?: number }

/**
 * Linha do tempo em abas: abas no topo (ponto + rótulo, ativa na cor primária com sublinhado),
 * à esquerda setas quadradas + texto grande da etapa ativa, à direita a lista vertical de rótulos
 * grandes (ativo em preto, restantes em cinza) com máscara de fade nas bordas. Autoplay opcional.
 */
export function TrajetoriaAbasClient({ etapas, autoplayMs }: Props) {
  const reduce = useReducedMotion()
  const [ativo, setAtivo] = useState(0)
  const [pausado, setPausado] = useState(false)
  const total = etapas.length

  const ir = useCallback((i: number) => setAtivo(((i % total) + total) % total), [total])
  const prox = useCallback(() => setAtivo((a) => indiceCircular(a, total, 1)), [total])
  const ant = useCallback(() => setAtivo((a) => indiceCircular(a, total, -1)), [total])

  useEffect(() => {
    if (!autoplayMs || pausado || reduce || total < 2) return
    const t = setInterval(prox, autoplayMs)
    return () => clearInterval(t)
  }, [autoplayMs, pausado, reduce, total, prox])

  const etapa = etapas[ativo]
  const ITEM_H = 72

  return (
    <div
      className="flex flex-col gap-10 lg:gap-14"
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocusCapture={() => setPausado(true)}
      onBlurCapture={() => setPausado(false)}
    >
      <div role="tablist" aria-label="Etapas" className="-mx-4 flex gap-6 overflow-x-auto px-4 lg:mx-0 lg:gap-10 lg:px-0 [scrollbar-width:none]">
        {etapas.map((e, i) => {
          const on = i === ativo
          return (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => ir(i)}
              className={cn(
                'ag-mono relative flex shrink-0 items-center gap-2 pb-3 text-[12px] transition-colors lg:text-[13px]',
                on ? 'text-[var(--ag-primary)]' : 'text-[var(--sec-muted)] hover:text-[var(--sec-fg)]',
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', on ? 'bg-[var(--ag-primary)]' : 'bg-current opacity-50')} aria-hidden />
              {e.titulo || e.rotulo}
              <span
                aria-hidden
                className={cn('absolute inset-x-0 bottom-0 h-[2px] transition-opacity', on ? 'bg-[var(--ag-primary)] opacity-100' : 'opacity-0')}
              />
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
        <div className="flex flex-col gap-8 lg:col-span-7">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={ant}
              aria-label="Etapa anterior"
              className="flex h-11 w-11 items-center justify-center border border-[rgb(var(--sec-border-rgb)/0.25)] transition-colors hover:bg-[var(--sec-fg)] hover:text-[var(--sec-bg)]"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={prox}
              aria-label="Próxima etapa"
              className="flex h-11 w-11 items-center justify-center border border-[rgb(var(--sec-border-rgb)/0.25)] transition-colors hover:bg-[var(--sec-fg)] hover:text-[var(--sec-bg)]"
            >
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
          <div className="relative min-h-[160px] lg:min-h-[220px]" aria-live="polite">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={ativo}
                initial={reduce ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col gap-4"
              >
                <span className="ag-mono text-[12px] text-[var(--ag-primary)]">{etapa.rotulo}</span>
                <p className="ag-heading max-w-[640px] text-[24px] leading-[1.25] lg:text-[32px]">{etapa.texto}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <div
            className="relative overflow-hidden"
            style={{
              height: ITEM_H * 5,
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
            }}
          >
            <motion.ul
              className="flex flex-col"
              animate={{ y: ITEM_H * 2 - ativo * ITEM_H }}
              transition={reduce ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              {etapas.map((e, i) => (
                <li key={i} style={{ height: ITEM_H }} className="flex items-center">
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => ir(i)}
                    className={cn(
                      'ag-heading text-left text-[40px] leading-none transition-colors duration-300 lg:text-[48px]',
                      i === ativo ? 'text-[var(--sec-fg)]' : 'text-[rgb(var(--sec-fg-rgb)/0.18)] hover:text-[rgb(var(--sec-fg-rgb)/0.4)]',
                    )}
                  >
                    {e.rotulo}
                  </button>
                </li>
              ))}
            </motion.ul>
          </div>
        </div>
      </div>
    </div>
  )
}
